import * as React from 'react';
import {cn} from '../../lib/cn';
import {useCombinedRef, useOutsideClick} from '../../hooks';

type SelectIconsBoxProps = {
  children: React.ReactNode;
  icons: string[];
  renderIcon: (iconName: string, color: string) => React.ReactNode;
  onSelect: (iconName: string) => void;
  columns?: number;
  iconsColor?: string;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  position?: 'left' | 'right' | 'center';
  className?: string;
  boxClassName?: string;
};

const FOCUSABLE_TRIGGER_SELECTOR =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), button:not([disabled])';

const SelectIconsBox = React.forwardRef<HTMLDivElement, SelectIconsBoxProps>(
  function SelectIconsBox(
    {
      children,
      icons,
      renderIcon,
      onSelect,
      columns = 4,
      iconsColor = 'var(--chekin-neutral-600)',
      isOpen: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      onFocus,
      position = 'left',
      className,
      boxClassName,
    },
    ref,
  ) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const combinedContainerRef = useCombinedRef(containerRef, ref);
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const setOpen = (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    };

    const handleToggle = (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('[data-icon-box-item]')) return;
      setOpen(!isOpen);
    };

    useOutsideClick({
      elementRef: containerRef,
      onOutsideClick: () => setOpen(false),
      isDisabled: !isOpen,
    });

    const handlePick = (iconName: string) => {
      onSelect(iconName);
      setOpen(false);
    };

    const handleContainerFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(event);
      if (event.target !== event.currentTarget) return;
      const focusable = event.currentTarget.querySelector<HTMLElement>(
        FOCUSABLE_TRIGGER_SELECTOR,
      );
      focusable?.focus();
    };

    return (
      <div
        ref={combinedContainerRef}
        tabIndex={-1}
        onFocus={handleContainerFocus}
        onClick={handleToggle}
        className={cn('relative inline-block outline-none', className)}
      >
        {children}
        {isOpen && (
          <div
            className={cn(
              'absolute top-full z-30 mt-1 grid max-h-[192px] overflow-y-auto rounded-[6px] bg-white p-6 shadow-[0_15px_15px_0_rgba(33,72,255,0.2)]',
              position === 'left' && 'left-0',
              position === 'right' && 'right-0',
              position === 'center' && 'left-1/2 -translate-x-1/2',
              boxClassName,
            )}
            style={{gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`}}
          >
            {icons.map(iconName => (
              <button
                key={iconName}
                type="button"
                data-icon-box-item
                onClick={() => handlePick(iconName)}
                className="relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 outline-none hover:bg-[var(--chekin-neutral-50)]"
                aria-label={iconName}
              >
                {renderIcon(iconName, iconsColor)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

SelectIconsBox.displayName = 'SelectIconsBox';

export {SelectIconsBox};
export type {SelectIconsBoxProps};
